/**
 * Camada 1 · a CENTRAL DE AJUDA, a tela de quem USA o produto.
 *
 * ===========================================================================
 * A ORDEM DOS BLOCOS E O DESENHO INTEIRO, e ela nao e arbitraria
 * ===========================================================================
 * Do manual de casos de uso de 01/09/2026, passo 13: *"a tela abre com a busca em primeiro
 * lugar e as categorias logo abaixo. ABRIR CONVERSA NAO E A PRIMEIRA OPCAO, E SIM A ULTIMA."*
 *
 * E o passo 16 diz por que: *"so chega aqui quem ja tentou se resolver sozinho"*. Inverter a
 * ordem, pondo o formulario no topo, transformaria a base de conhecimento em decoracao: a
 * pessoa abriria conversa antes de procurar, e a equipe responderia pela centesima vez o que
 * ja esta escrito.
 *
 *   1. Busca                 (passo 14)
 *   2. Categorias            (passo 15)
 *   3. Nao encontrei         (passo 16) → abre o formulario
 *   4. Formulario            (passos 17, 18 e 21)
 *   5. Minhas solicitacoes   (passos 19 e 20)
 *
 * ===========================================================================
 * O VOCABULARIO E GENERALIZADO
 * ===========================================================================
 * Pedido dele em 03/09/2026: *"NAO TEM TERÁ NADA A VER COM MEDICINA"*. Nao existe aqui
 * paciente, medico, clinica nem consulta. O que varia por produto e o CONTEUDO da base, que
 * entra pelo namespace, e nao esta tela.
 *
 * ESTE ARQUIVO NAO DESENHA CHASSI. Rail, topbar, tema e idioma moram em @complexity/layout,
 * e o `guarda-fronteira.sh` recusa a escrita que os duplicasse aqui.
 */

import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  Botao,
  Campo,
  CampoBusca,
  EstadoVazio,
  Markdown,
  Selecao,
  CampoAnexo,
  useIdiomaVivo,
  type ImagemReduzida,
} from '@complexity/ui';
import { t } from '@complexity/i18n';
import type { ArtigoAjuda } from '../index.js';
import {
  ROTULO_DA_SITUACAO,
  ROTULO_DO_TIPO,
  type CategoriaAjuda,
  type ConversaAjuda,
  type FonteCentral,
  type TipoDeConversa, type AnexoDaConversa} from '../central.js';

export interface CentralDeAjudaProps {
  /** Vem do ProductConfig. Obrigatorio e sem padrao: dois produtos com o mesmo namespace
   *  passam a ver a ajuda um do outro, e isso e vazamento de conteudo, nao recurso. */
  readonly namespace: string;
  readonly fonte: FonteCentral;
  /**
   * O ASSUNTO de um pedido à equipe que chegou pronto, vindo da busca sem resultado da gaveta de
   * Ajuda. Presente, a Central abre com o formulário aberto e o assunto preenchido. Nasceu em
   * 11/09/2026, quando o botão "Falar com a nossa equipe" passou a funcionar, por decisão do
   * fundador em enquete.
   */
  readonly assuntoInicial?: string;
}

/** A espera antes de buscar. O MESMO valor do `PainelAjuda`, de proposito: dois tempos para
 *  a mesma busca fariam a Central parecer mais lenta sem nenhuma razao visivel. */
const ESPERA_MS = 320;

/** Abaixo disto a busca nao dispara: uma letra casa com quase tudo e a lista vira ruido. */
const MINIMO_PARA_BUSCAR = 2;

/**
 * OS VALORES do select de tipo, que sao os do dominio e NAO mudam com o idioma.
 *
 * SO OS VALORES MORAM AQUI. O rotulo de cada um e montado DENTRO do componente, e a razao e
 * a mesma que tirou `ROTULO_DO_TIPO` do ponto de uso: constante de modulo e avaliada UMA vez,
 * na carga, antes de existir qualquer escolha de idioma. Montado aqui, o select ficaria em
 * portugues para sempre, e a troca para EN mudaria a tela inteira menos ele.
 */
const VALORES_DE_TIPO = Object.keys(ROTULO_DO_TIPO) as TipoDeConversa[];

/** Data no formato que a pessoa le, sem hora: a hora nao muda nenhuma decisao dela. */
function formatarData(iso: string): string {
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? '' : data.toLocaleDateString('pt-BR');
}

/**
 * A CAUSA REAL, e nao um "nao respondeu" que apaga tudo.
 *
 * O QUE COMPROU ISTO, medido em 06/09/2026: a tela dizia "A base de conhecimento nao
 * respondeu" com o servico DE PE e respondendo 200 a quem tinha cliente. A causa era um
 * 401 por falta de `X-Tenant-Id`, e a mensagem apagava exatamente o que permitiria
 * descobrir isso. O fundador passou a manha achando que a base estava fora.
 *
 * A FRASE GENERICA CONTINUA COMO ULTIMO RECURSO, para erro sem mensagem (uma falha de
 * rede crua, por exemplo) nao virar tela vazia. O que muda e a ORDEM: a causa primeiro.
 */
/**
 * AS MENSAGENS QUE A BIBLIOTECA ESCREVE, e que NUNCA podem chegar a tela.
 *
 * MEDIDO EM 07/09/2026, 20:08, na tela do VOE: a Central mostrava a palavra
 * `Failed to fetch` em vermelho, logo abaixo do campo de busca. Ela nao foi escrita por
 * ninguem desta casa: e o texto que o Chrome poe em `TypeError` quando a conexao nao sai
 * da maquina. Cada navegador escreve a sua, todas em ingles, e nenhuma delas diz a quem
 * opera o que fazer a seguir.
 *
 * A LISTA E POR PREFIXO E EM MINUSCULAS porque as variantes divergem entre motores:
 * Chrome diz `Failed to fetch`, Firefox diz `NetworkError when attempting to fetch
 * resource.`, Safari diz `Load failed`, e o undici do Node diz `fetch failed`. Casar a
 * frase inteira deixaria tres dos quatro passarem.
 */
const MENSAGENS_DE_BIBLIOTECA: readonly string[] = [
  'failed to fetch',
  'fetch failed',
  'networkerror',
  'load failed',
  'the internet connection appears to be offline',
  'the operation was aborted',
  'network request failed',
  'typeerror',
  /**
   * AS QUATRO ABAIXO SAO DO FRAMEWORK DO SERVIDOR, e entraram em 13/09/2026.
   *
   * O DEFEITO, medido na tela do medico-concierge: a Central mostrava `Internal server
   * error` em vermelho. A frase chega pelo CORPO da resposta, e por isso escapava: o
   * `pedir` de `fonte-central-http.ts:205` le `message` do JSON porque o servico escreve
   * ali frases boas, como "Token de acesso ausente.". Quando NINGUEM trata a excecao, quem
   * preenche esse mesmo campo e o filtro padrao do Nest, com `{"statusCode":500,
   * "message":"Internal server error"}`.
   *
   * ENTAO O CRITERIO DO ARQUIVO CONTINUA O MESMO, e so o alcance cresce: vale QUEM ESCREVEU
   * A FRASE, e nao se ela veio do corpo ou do navegador. Estas quatro nao foram escritas por
   * ninguem desta casa, estao em ingles, e nao dizem a quem opera o que fazer a seguir.
   *
   * A CAUSA REAL NAO SE PERDE: ela continua no console, com o status, por
   * `fonte-central-http.ts:194`. O que muda e a tela, que passa a dizer a frase honesta
   * desta casa em vez do jargao de quem so transportou o erro.
   */
  'internal server error',
  'service unavailable',
  'bad gateway',
  'gateway timeout',
];

/**
 * A CAUSA REAL, e nao um "nao respondeu" que apaga tudo.
 *
 * O QUE COMPROU A PRIMEIRA VERSAO, medido em 06/09/2026: a tela dizia "A base de
 * conhecimento nao respondeu" com o servico DE PE e respondendo 200 a quem tinha cliente.
 * A causa era um 401 por falta de `X-Tenant-Id`, e a mensagem generica apagava exatamente
 * o que permitiria descobrir isso. O fundador passou a manha achando que a base estava
 * fora.
 *
 * O QUE COMPROU ESTA SEGUNDA, medido em 07/09/2026: a correcao acima passou a mostrar
 * `erro.message` SEM FILTRO, e com o servico fora do ar a tela exibia `Failed to fetch`.
 * Trocou-se um defeito por outro: antes escondia a causa, agora mostra jargao de
 * biblioteca, em ingles, para quem opera um voo.
 *
 * ENTAO O CRITERIO NAO E "generica ou real": e QUEM ESCREVEU A FRASE. Mensagem redigida
 * por esta casa (o `ErroApi` e os erros de dominio) diz o que houve e o que fazer, e vai
 * para a tela inteira. Mensagem que a plataforma web escreveu descreve o transporte, nao
 * o problema de quem le, e cai na generica.
 *
 * ISTO CUMPRE A REGRA DURA DE 07/09/2026, e ela vale nos dois sentidos: ausencia de dado
 * nao pode parecer defeito do sistema, E defeito do sistema nao pode chegar a tela como
 * texto de biblioteca. As duas metades protegem a mesma coisa, que e a pessoa conseguir
 * decidir o que fazer a seguir.
 */
function causaDe(erro: unknown, generica: string): string {
  if (!(erro instanceof Error)) return generica;

  const mensagem = erro.message.trim();
  if (mensagem === '') return generica;

  // A COMPARACAO E EM MINUSCULAS E POR PREFIXO/CONTEUDO, e nao exata: ver o comentario da
  // lista acima. `includes` e nao `startsWith` porque algumas chegam prefixadas pelo nome
  // do tipo, como `TypeError: Failed to fetch`.
  const emMinusculas = mensagem.toLowerCase();
  if (MENSAGENS_DE_BIBLIOTECA.some((m) => emMinusculas.includes(m))) return generica;

  return mensagem;
}

export function CentralDeAjuda({ namespace, fonte, assuntoInicial }: CentralDeAjudaProps) {
  /**
   * O IDIOMA VIVO, e ele e a PRIMEIRA linha do corpo de proposito.
   *
   * SEM ELE, `t()` roda uma vez na montagem e a tela fica no idioma daquele instante. Clicar
   * EN gravaria o cookie, moveria o `aria-pressed` e trocaria o `lang` do documento, e NENHUM
   * texto desta tela mudaria ate a proxima navegacao. Isso e "metade da interface trocando"
   * com outra roupa, e a regra dura diz que e pior que seletor nenhum, porque a pessoa passa
   * a desconfiar do que leu.
   *
   * O VALOR E USADO, e nao so o efeito: `OPCOES_DE_TIPO` abaixo depende dele para recalcular
   * os rotulos do select quando o idioma muda.
   */
  const idioma = useIdiomaVivo();

  // --- BLOCOS 1 e 2 · busca e categorias -----------------------------------
  const [termo, definirTermo] = useState('');
  const [categorias, definirCategorias] = useState<readonly CategoriaAjuda[]>([]);
  const [categoriaAberta, definirCategoriaAberta] = useState<CategoriaAjuda | null>(null);
  const [artigos, definirArtigos] = useState<readonly ArtigoAjuda[]>([]);
  const [artigoAberto, definirArtigoAberto] = useState<ArtigoAjuda | null>(null);
  const [corpo, definirCorpo] = useState<string | null>(null);
  /** Fonte fora do ar NAO e busca sem resultado, e por isso e estado proprio. Confundir os
   *  dois faria a tela dizer "nenhum artigo" quando o servico caiu. */
  const [indisponivel, definirIndisponivel] = useState<string | null>(null);

  const idBusca = useId();

  /*
   * O IDIOMA ENTRA EM TODA LEITURA DE CONHECIMENTO, e a troca RECARREGA o que esta na tela
   * (ADR 0060). Sem isso, clicar EN traduziria o chassi e deixaria categoria, lista e artigo
   * aberto em portugues ate a pessoa navegar, que e a "metade da interface trocando".
   *
   * O QUE ESTA ABERTO E GUARDADO PELO ID, e o objeto e trocado pelo da resposta nova: o objeto
   * antigo carrega o titulo no idioma anterior, e mante-lo faria o cabecalho do artigo ficar em
   * portugues sobre um corpo ja em ingles.
   */
  const idCategoriaAberta = categoriaAberta?.id ?? null;
  const idArtigoAberto = artigoAberto?.id ?? null;

  // As categorias, na abertura e a cada troca de idioma. A CONTAGEM ZERO FICA DE FORA aqui, e
  // nao no servidor: o manual diz que a categoria "so aparece para o usuario quando tiver
  // artigo", e filtrar na tela mantem essa decisao visivel para quem le o codigo.
  useEffect(() => {
    if (!namespace) return;
    let vivo = true;
    void fonte
      .categorias(namespace, idioma)
      .then((lista) => {
        if (!vivo) return;
        /**
         * A LINHA VERMELHA SAI QUANDO A BASE VOLTA, e ate 13/09/2026 ela NUNCA saia.
         *
         * `indisponivel` so era escrito, em tres lugares, e limpo em nenhum. Uma falha
         * marcava a tela para o resto da sessao: com o servico de volta e as categorias
         * desenhadas, a pessoa continuava lendo "A base de conhecimento nao respondeu"
         * acima de uma lista cheia. E a mesma contradicao do estado vazio, invertida, e
         * pior de perceber, porque agora ha conteudo na tela dando razao a quem duvida
         * do aviso.
         *
         * AQUI E NAO NO `catch` DE CADA LEITURA: esta e a chamada que sustenta a frase
         * sobre a BASE inteira. Sucesso dela e a prova de que a base respondeu.
         */
        definirIndisponivel(null);
        const visiveis = lista.filter((c) => c.artigos > 0);
        definirCategorias(visiveis);
        // A categoria aberta ganha o nome no idioma novo, achada pelo id.
        definirCategoriaAberta((aberta) =>
          aberta ? (visiveis.find((c) => c.id === aberta.id) ?? aberta) : aberta,
        );
      })
      .catch((erro: unknown) => {
        if (vivo) definirIndisponivel(causaDe(erro, 'A base de conhecimento não respondeu.'));
      });
    return () => {
      vivo = false;
    };
  }, [namespace, fonte, idioma]);

  // A busca, com espera. Abaixo do minimo ela VOLTA para as categorias em vez de mostrar
  // lista vazia, que e o comportamento descrito no proprio legado. O idioma nas dependencias
  // refaz a busca em curso quando a pessoa troca PT e EN com resultado na tela.
  useEffect(() => {
    if (termo.trim().length < MINIMO_PARA_BUSCAR) {
      if (!categoriaAberta) definirArtigos([]);
      return;
    }
    let vivo = true;
    const relogio = setTimeout(() => {
      void fonte
        .buscar(namespace, termo.trim(), idioma)
        .then((achados) => {
          if (!vivo) return;
          definirCategoriaAberta(null);
          definirArtigos(achados);
          // O artigo aberto a partir da busca ganha o titulo no idioma novo, pelo id.
          definirArtigoAberto((aberto) =>
            aberto ? (achados.find((a) => a.id === aberto.id) ?? aberto) : aberto,
          );
        })
        .catch((erro: unknown) => {
          if (vivo)
            definirIndisponivel(
              causaDe(erro, 'A busca não respondeu. Tente novamente em instantes.'),
            );
        });
    }, ESPERA_MS);
    return () => {
      vivo = false;
      clearTimeout(relogio);
    };
  }, [termo, namespace, fonte, categoriaAberta, idioma]);

  // Os artigos da categoria aberta. MUDOU DE LUGAR, do clique para um efeito, e a razao e o
  // idioma: no clique a leitura acontecia uma vez so, e a troca PT e EN nao tinha como alcanca-la.
  // Chaveado pelo ID, o efeito roda ao abrir e a cada troca, e nao a cada redesenho.
  useEffect(() => {
    if (!namespace || idCategoriaAberta === null) return;
    let vivo = true;
    void fonte
      .artigosDaCategoria(namespace, idCategoriaAberta, idioma)
      .then((lista) => {
        if (!vivo) return;
        definirArtigos(lista);
        definirArtigoAberto((aberto) =>
          aberto ? (lista.find((a) => a.id === aberto.id) ?? aberto) : aberto,
        );
      })
      .catch(() => {
        if (vivo) definirIndisponivel('Os artigos desta categoria não responderam.');
      });
    return () => {
      vivo = false;
    };
  }, [namespace, fonte, idCategoriaAberta, idioma]);

  // O corpo do artigo aberto, pelo mesmo motivo: chaveado pelo id e pelo idioma, para o texto
  // acompanhar a troca e nunca ficar num idioma diferente do titulo acima dele.
  useEffect(() => {
    if (!namespace || idArtigoAberto === null) return;
    let vivo = true;
    definirCorpo(null);
    void fonte
      .corpo(namespace, idArtigoAberto, idioma)
      .then((texto) => {
        if (vivo) definirCorpo(texto);
      })
      .catch(() => {
        if (vivo) definirCorpo(null);
      });
    return () => {
      vivo = false;
    };
  }, [namespace, fonte, idArtigoAberto, idioma]);

  const abrirCategoria = useCallback((categoria: CategoriaAjuda) => {
    definirTermo('');
    definirCategoriaAberta(categoria);
    definirArtigoAberto(null);
  }, []);

  const abrirArtigo = useCallback((artigo: ArtigoAjuda) => {
    definirArtigoAberto(artigo);
  }, []);

  // --- BLOCO 4 · o formulario de conversa ----------------------------------
  const [formularioAberto, definirFormularioAberto] = useState(Boolean(assuntoInicial));
  const [tipo, definirTipo] = useState<TipoDeConversa | ''>('');
  const [assunto, definirAssunto] = useState(assuntoInicial ?? '');
  const [mensagem, definirMensagem] = useState('');
  /** O texto do campo de categoria. Ele serve aos DOIS caminhos: escolher da lista e sugerir
   *  uma nova (passo 21). Qual dos dois vale se decide por `categoriaEscolhida`. */
  const [textoCategoria, definirTextoCategoria] = useState('');
  const [categoriaEscolhida, definirCategoriaEscolhida] = useState<CategoriaAjuda | null>(null);
  const [enviando, definirEnviando] = useState(false);
  const [erroDoEnvio, definirErroDoEnvio] = useState<string | null>(null);
  /**
   * As imagens escolhidas, JA REDUZIDAS pelo `CampoAnexo`.
   *
   * Elas ficam AQUI e nao dentro do campo porque quem ENVIA e esta tela: o campo entrega o
   * base64 pronto e nao sabe para onde ele vai, e essa fronteira e o que permite o mesmo
   * campo servir a Ajuda e o Super Admin, que atravessam autorizacoes diferentes.
   */
  const [anexos, definirAnexos] = useState<readonly ImagemReduzida[]>([]);

  /**
   * Os anexos JA GRAVADOS nas conversas, por conversa.
   *
   * ELES SAO OUTRA COISA que o `anexos` acima: aquele sao as imagens que a pessoa esta
   * ESCOLHENDO para enviar agora; estes sao os que ja estao nas mensagens. Os dois nomes
   * dizem qual e qual, porque confundi-los foi o que o compilador pegou na tela do admin.
   */
  const [anexosPorConversa, definirAnexosPorConversa] = useState<
    Readonly<Record<string, readonly AnexoDaConversa[]>>
  >({});
  /** Qual anexo esta sendo aberto, para o botao dizer que esta trabalhando. */
  const [abrindoAnexo, definirAbrindoAnexo] = useState<string | null>(null);

  // --- BLOCO 5 · minhas solicitacoes ---------------------------------------
  const [conversas, definirConversas] = useState<readonly ConversaAjuda[]>([]);

  useEffect(() => {
    if (!namespace) return;
    let vivo = true;
    void fonte
      .minhasConversas(namespace)
      .then((lista) => {
        if (vivo) definirConversas(lista);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [namespace, fonte]);

  /**
   * A CATEGORIA que viaja com a conversa.
   *
   * A EXCLUSAO E AQUI, e nao no servidor: escolheu da lista, viaja o id e a sugestao fica
   * nula; digitou algo que nao casa com nenhuma, viaja a sugestao e o id fica nulo. Mandar
   * os dois faria o administrador ver sugestao pendente para categoria que ja existe, que e
   * o passo 12 pedindo decisao sobre nada.
   */
  /** Os rotulos do select, traduzidos NO PONTO DE USO. `idioma` esta nas dependencias
   *  porque e a troca dele que obriga o recalculo: sem isso o React devolveria a lista
   *  memorizada em portugues depois de a pessoa escolher EN. */
  const opcoesDeTipo = useMemo(
    () => VALORES_DE_TIPO.map((valor) => ({ valor, rotulo: t(ROTULO_DO_TIPO[valor]) })),
    [idioma],
  );

  const categoriaDaConversa = useMemo(() => {
    const texto = textoCategoria.trim();
    if (categoriaEscolhida && categoriaEscolhida.nome === texto) {
      return { categoriaId: categoriaEscolhida.id, categoriaSugerida: null };
    }
    return { categoriaId: null, categoriaSugerida: texto === '' ? null : texto };
  }, [categoriaEscolhida, textoCategoria]);

  const podeEnviar = tipo !== '' && assunto.trim() !== '' && mensagem.trim() !== '' && !enviando;

  /**
   * BUSCA os anexos de cada conversa, uma chamada por conversa.
   *
   * UMA POR CONVERSA e nao uma por MENSAGEM: a rota `/conversas/:id/anexos` devolve os da
   * conversa inteira, e sem ela seriam dez chamadas para uma conversa de dez mensagens,
   * com a maioria voltando vazia.
   *
   * SO PARA AS QUE AINDA NAO FORAM BUSCADAS: sem esta guarda, cada render dispararia tudo
   * de novo, e a lista se redesenha a cada resposta que chega.
   */
  useEffect(() => {
    if (!fonte.anexosDaConversa) return;
    const buscar = fonte.anexosDaConversa.bind(fonte);
    let vivo = true;
    for (const conversa of conversas) {
      if (anexosPorConversa[conversa.id] !== undefined) continue;
      void buscar(namespace, conversa.id)
        .then((lista) => {
          if (!vivo) return;
          definirAnexosPorConversa((atuais) => ({ ...atuais, [conversa.id]: lista }));
        })
        .catch(() => {
          // Uma conversa sem anexo e o caso comum, e uma falha aqui nao pode derrubar a
          // tela: a lista fica vazia para AQUELA conversa, e o resto continua desenhando.
          if (!vivo) return;
          definirAnexosPorConversa((atuais) => ({ ...atuais, [conversa.id]: [] }));
        });
    }
    return () => {
      vivo = false;
    };
  }, [conversas, namespace, fonte, anexosPorConversa]);

  /**
   * ABRE o anexo numa aba nova.
   *
   * O conteudo vem em BASE64 e nao por URL direta: a chamada precisa dos cabecalhos de
   * sessao, e uma tag de imagem apontando para a URL nao carrega cabecalho nenhum.
   *
   * A URL do blob e revogada depois de um minuto, e nao na hora: revogar imediatamente
   * mata a aba que acabou de abrir, porque o navegador ainda esta carregando.
   */
  const abrirAnexo = useCallback(
    (anexoId: string) => {
      if (!fonte.anexo || abrindoAnexo) return;
      const ler = fonte.anexo.bind(fonte);
      definirAbrindoAnexo(anexoId);
      void ler(namespace, anexoId)
        .then((a) => {
          const bytes = Uint8Array.from(atob(a.base64), (c) => c.charCodeAt(0));
          const url = URL.createObjectURL(new Blob([bytes], { type: a.tipoConteudo }));
          window.open(url, '_blank', 'noopener');
          window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        })
        .finally(() => definirAbrindoAnexo(null));
    },
    [fonte, namespace, abrindoAnexo],
  );

  const enviar = useCallback(() => {
    // A CHECAGEM E POR `podeEnviar`, e nao por `tipo === ''` de novo: `podeEnviar` ja
    // carrega essa condicao, e repeti-la fazia o compilador estreitar o tipo e acusar
    // comparacao sem sobreposicao. Uma condicao, num lugar so.
    if (!podeEnviar) return;
    const tipoEscolhido = tipo as TipoDeConversa;
    definirEnviando(true);
    definirErroDoEnvio(null);
    void fonte
      .abrirConversa(namespace, {
        tipo: tipoEscolhido,
        assunto: assunto.trim(),
        mensagem: mensagem.trim(),
        ...categoriaDaConversa,
      })
      .then(async (criada) => {
        /*
          OS ANEXOS VAO DEPOIS, e nao junto, porque o anexo pertence a uma MENSAGEM e nao a
          uma conversa: so depois de abrir e que existe mensagem para anexar. O id dela sai
          do PRIMEIRO turno da conversa criada, que e justamente o texto que a pessoa
          acabou de escrever.

          A FALHA DO ANEXO NAO APAGA A MENSAGEM, e por isso ela tem `catch` proprio: quando
          esta linha roda, o texto JA CHEGOU. Deixar o erro subir faria a tela dizer "nao
          foi possivel enviar" sobre uma mensagem que foi enviada, e a pessoa mandaria de
          novo.
        */
        const primeiroTurno = criada.turnos[0];
        if (anexos.length > 0 && fonte.anexar && primeiroTurno) {
          try {
            await fonte.anexar(
              namespace,
              criada.id,
              primeiroTurno.id,
              anexos.map((a) => ({
                nomeArquivo: a.nomeArquivo,
                tipoConteudo: a.tipoConteudo,
                base64: a.base64,
              })),
            );
          } catch {
            definirErroDoEnvio(
              'A sua mensagem foi enviada, mas não consegui anexar as imagens. Você pode respondê-la com as imagens depois.',
            );
          }
        }

        // A conversa nova entra NO TOPO, e o formulario fecha e se limpa. Recarregar a lista
        // inteira do servidor faria a pessoa esperar por um dado que ja esta na mao.
        definirConversas((atuais) => [criada, ...atuais]);
        definirFormularioAberto(false);
        definirTipo('');
        definirAssunto('');
        definirMensagem('');
        definirTextoCategoria('');
        definirCategoriaEscolhida(null);
        definirAnexos([]);
      })
      .catch(() => definirErroDoEnvio('Não foi possível enviar agora. Tente novamente.'))
      .finally(() => definirEnviando(false));
  }, [podeEnviar, tipo, assunto, mensagem, categoriaDaConversa, namespace, fonte, anexos]);

  // O artigo aberto ocupa a Central inteira: quem esta lendo a resposta nao precisa da busca
  // e das categorias competindo por atencao.
  if (artigoAberto) {
    return (
      /**
       * `<article>` E NAO `<div>`, e a troca serve a duas coisas de uma vez.
       *
       * PARA QUEM USA LEITOR DE TELA: este bloco e um documento autonomo, com titulo
       * proprio, e o elemento diz isso. Num `div` a pessoa nao tem como saltar para ele
       * nem saber onde o artigo comeca e termina.
       *
       * PARA A SUITE: o caso "o artigo abre com o corpo, e nao apenas com o titulo" nao
       * tinha o que medir. Ele cobrava uma FRASE do conteudo em memoria, e ficou vermelho
       * quando a Central passou a ler o banco em 06/09/2026. Com o marco semantico, ele
       * mede a promessa (ha corpo alem do titulo) em vez de decorar um texto que muda a
       * cada revisao editorial.
       */
      <article className="flex flex-col gap-cx-4">
        <Botao
          variante="texto"
          tamanho="sm"
          className="self-start"
          onClick={() => definirArtigoAberto(null)}
        >
          {t('Voltar')}
        </Botao>
        <h2 className="text-cx-xl font-semibold text-texto">{artigoAberto.titulo}</h2>
        {/* A QUEDA PARA O ORIGINAL E DITA, e nao escondida (ADR 0060): pedido em ingles, o
            artigo ainda sem traducao chega em portugues, e sem esta linha a pessoa acharia que
            a troca de idioma falhou. `idioma` ausente (fonte em memoria) nao tem o que avisar. */}
        {artigoAberto.idioma !== undefined && artigoAberto.idioma !== idioma ? (
          <p className="text-cx-sm text-texto-suave">{t('Disponível só em português')}</p>
        ) : null}
        {corpo === null ? (
          <p className="text-cx-sm text-texto-suave">{t('Carregando…')}</p>
        ) : (
          <Markdown texto={corpo} />
        )}
      </article>
    );
  }

  return (
    <div className="flex flex-col gap-cx-5">
      <header className="flex flex-col gap-cx-1">
        <h2 className="text-cx-xl font-semibold text-texto">{t('Central de Ajuda')}</h2>
        <p className="text-cx-sm text-texto-suave">
          {t('Base de conhecimento da plataforma: busque e veja a resposta na hora, sem esperar.')}
        </p>
      </header>

      {/* BLOCO 1 · BUSCA, primeira de propósito (passo 13). */}
      <div className="flex flex-col gap-cx-1">
        <label htmlFor={idBusca} className="sr-only">
          {t('Buscar na ajuda')}
        </label>
        <input
          id={idBusca}
          type="search"
          value={termo}
          onChange={(evento) => definirTermo(evento.target.value)}
          placeholder={t('Buscar ajuda…')}
          className="w-full rounded-cx-md border border-borda bg-fundo px-cx-3 py-cx-2 text-cx-base text-texto placeholder:text-texto-fraco focus:border-marca focus:outline-none focus:ring-2 focus:ring-marca/40"
        />
      </div>

      {/* A TRADUCAO ACONTECE AQUI, e nao na hora de guardar o estado: guardada traduzida,
          a mensagem congelaria o idioma do instante do erro e a troca nao a alcancaria. E
          `t()` devolve a propria chave quando nao ha entrada, entao mensagem vinda da API
          atravessa intacta em vez de virar texto vazio. */}
      {indisponivel ? <p className="text-cx-sm text-erro">{t(indisponivel)}</p> : null}

      {/* Resultado da busca OU artigos da categoria escolhida. */}
      {artigos.length > 0 ? (
        <section className="flex flex-col gap-cx-2">
          <h3 className="text-cx-xs font-semibold uppercase tracking-wide text-texto-fraco">
            {/* O NOME DA CATEGORIA NAO PASSA POR `t()`: ele vem do banco, e desde o ADR 0060
                ja chega no idioma pedido, lido de `conhecimento_categorias_traducoes`. Passa-lo
                por um dicionario de frases criaria uma segunda traducao do mesmo nome. */}
            {categoriaAberta ? categoriaAberta.nome : t('Resultados')}
          </h3>
          <ul className="flex flex-col gap-cx-2">
            {artigos.map((artigo) => (
              <li key={artigo.id}>
                <button
                  type="button"
                  onClick={() => abrirArtigo(artigo)}
                  className="w-full rounded-cx-md border border-borda bg-superficie p-cx-3 text-left transition hover:border-marca focus:border-marca focus:outline-none focus:ring-2 focus:ring-marca/40"
                >
                  <span className="block text-cx-base font-medium text-texto">{artigo.titulo}</span>
                  <span className="mt-cx-1 block text-cx-sm text-texto-suave">{artigo.resumo}</span>
                  {/* A queda para o original e dita tambem na LISTA, e nao so no artigo aberto:
                      e aqui que a pessoa decide o que abrir. */}
                  {artigo.idioma !== undefined && artigo.idioma !== idioma ? (
                    <span className="mt-cx-1 block text-cx-xs text-texto-fraco">
                      {t('Disponível só em português')}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Busca sem resultado: dito, e nao escondido. E daqui que o caminho de conversa ganha
          sentido, porque a pessoa acabou de tentar e nao achou. */}
      {termo.trim().length >= MINIMO_PARA_BUSCAR && artigos.length === 0 && !indisponivel ? (
        <EstadoVazio titulo={t('Nenhum artigo para essa busca')}>
          {t('Tente outras palavras, ou descreva o problema abaixo para a equipe.')}
        </EstadoVazio>
      ) : null}

      {/* BLOCO 2 · CATEGORIAS, quando nao ha busca em curso (passo 15). */}
      {termo.trim().length < MINIMO_PARA_BUSCAR && !categoriaAberta ? (
        <section className="flex flex-col gap-cx-2">
          <h3 className="text-cx-xs font-semibold uppercase tracking-wide text-texto-fraco">
            {t('Categorias')}
          </h3>
          {/* AUSENCIA E FALHA SAO ESTADOS DIFERENTES, e `!indisponivel` e o que os separa.
              MEDIDO EM 13/09/2026, na tela: com a base fora, esta secao mostrava "Nenhuma
              categoria publicada ainda" LOGO ABAIXO da linha vermelha de indisponibilidade.
              As duas frases na mesma tela se contradizem, e a segunda e a mais cara: ela
              manda procurar o curador para publicar artigo que ja existe.
              A causa e estrutural e nao de texto: `categorias` nasce `[]`, e o `catch` nao
              tem como distinguir "voltou vazio" de "nao voltou". Quem sabe a diferenca e o
              `indisponivel`, e por isso ele entra na condicao.
              O bloco de busca sem resultado, acima, ja fazia isto desde sempre: o que havia
              aqui era a metade que ninguem tinha ligado. */}
          {categorias.length === 0 && !indisponivel ? (
            <EstadoVazio titulo={t('Nenhuma categoria publicada ainda')}>
              {t('Quando a equipe publicar o primeiro artigo, ele aparece aqui.')}
            </EstadoVazio>
          ) : (
            <ul className="grid grid-cols-1 gap-cx-2 sm:grid-cols-2 lg:grid-cols-3">
              {categorias.map((categoria) => (
                <li key={categoria.id}>
                  <button
                    type="button"
                    onClick={() => abrirCategoria(categoria)}
                    className="flex w-full items-center justify-between gap-cx-2 rounded-cx-md border border-borda bg-superficie px-cx-3 py-cx-3 text-left text-cx-base text-texto transition hover:border-marca focus:border-marca focus:outline-none focus:ring-2 focus:ring-marca/40"
                  >
                    <span>{categoria.nome}</span>
                    <span aria-hidden="true" className="text-texto-fraco">
                      ›
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {categoriaAberta ? (
        <Botao
          variante="texto"
          tamanho="sm"
          className="self-start"
          onClick={() => {
            definirCategoriaAberta(null);
            definirArtigos([]);
          }}
        >
          {t('Todas as categorias')}
        </Botao>
      ) : null}

      {/* BLOCO 3 · NAO ENCONTREI (passo 16). Fica DEPOIS da busca e das categorias, e o
          manual e explicito sobre isso ser proposital. */}
      <section className="flex flex-col gap-cx-3 rounded-cx-md border border-borda bg-superficie p-cx-3">
        <div className="flex flex-wrap items-center justify-between gap-cx-2">
          <div className="flex flex-col">
            <span className="text-cx-base font-medium text-texto">
              {t('Não encontrei o que precisava')}
            </span>
            <span className="text-cx-sm text-texto-suave">
              {t('Descreva que a gente responde por aqui.')}
            </span>
          </div>
          <Botao
            variante={formularioAberto ? 'texto' : 'contorno'}
            tamanho="sm"
            onClick={() => definirFormularioAberto((aberto) => !aberto)}
          >
            {formularioAberto ? t('Fechar') : t('Descrever o problema')}
          </Botao>
        </div>

        {/* BLOCO 4 · O FORMULARIO (passos 17, 18 e 21). */}
        {formularioAberto ? (
          <div className="flex flex-col gap-cx-3 border-t border-borda pt-cx-3">
            {/* TIPO e CATEGORIA na MESMA LINHA: dois campos curtos e relacionados, e o padrao
                da casa e linha compacta, nao campos empilhados em largura total. */}
            <div className="flex flex-wrap items-end gap-cx-3">
              <div className="min-w-[12rem] flex-1">
                <Selecao
                  rotulo={t('Tipo')}
                  obrigatorio
                  vazio={t('Selecione…')}
                  opcoes={opcoesDeTipo}
                  value={tipo}
                  onChange={(evento) => definirTipo(evento.target.value as TipoDeConversa)}
                />
              </div>
              <div className="min-w-[14rem] flex-1">
                {/* O MESMO CAMPO faz as duas coisas do passo 21: escolher da lista, ou
                    escrever o nome de uma que falta. E por isso ele nao e um select. */}
                <CampoBusca<CategoriaAjuda>
                  rotulo={t('Categoria')}
                  valor={textoCategoria}
                  aoMudar={(texto) => {
                    definirTextoCategoria(texto);
                    definirCategoriaEscolhida(null);
                  }}
                  aoEscolher={(categoria) => {
                    definirCategoriaEscolhida(categoria);
                    definirTextoCategoria(categoria.nome);
                  }}
                  buscar={(t) =>
                    categorias.filter((c) => c.nome.toLowerCase().includes(t.toLowerCase()))
                  }
                  renderizarItem={(categoria) => categoria.nome}
                  chaveDoItem={(categoria) => categoria.id}
                  placeholder={t('Escolha da lista ou sugira uma nova')}
                  testid="ajuda-categoria"
                />
              </div>
            </div>

            <Campo
              rotulo={t('Assunto')}
              obrigatorio
              value={assunto}
              onChange={(evento) => definirAssunto(evento.target.value)}
              placeholder={t('Resuma em poucas palavras')}
            />

            <label className="flex flex-col gap-cx-1">
              <span className="text-cx-sm font-medium text-texto">
                {t('Mensagem')} <span aria-hidden="true">*</span>
              </span>
              <textarea
                required
                rows={5}
                value={mensagem}
                onChange={(evento) => definirMensagem(evento.target.value)}
                placeholder={t('Conte com detalhes o que você precisa…')}
                className="w-full rounded-cx-md border border-borda bg-fundo px-cx-3 py-cx-2 text-cx-base text-texto placeholder:text-texto-fraco focus:border-marca focus:outline-none focus:ring-2 focus:ring-marca/40"
              />
            </label>

            {/*
              O ANEXO, logo abaixo da mensagem, porque e dela que ele fala. Pedido dele em
              07/09/2026: "QUANDO O USUARIO NAO ENCONTRAR E FOR MANDAR A MENSAGEM TEM QUE
              PODER COLOCAR ANEXO, QQ TIPO DE IMAGEM DE QQ TAMANHO."

              🔴 ELE SO APARECE SE A FONTE SOUBER ANEXAR, e essa checagem nao e zelo: o
              contrato declara `anexar?` como OPCIONAL porque a fonte EM MEMORIA, que e a
              degradacao quando falta `VITE_CX_API_KNOWLEDGE`, nao o implementa. Sem esta
              linha, o campo apareceria naquele modo e o envio falharia calado, que e pior
              que nao oferecer.

              NAO HA AVISO DE TAMANHO NEM DE FORMATO, e a ausencia e a decisao dele: o
              campo aceita `image/*` e reduz o que nao couber. Escrever um limite aqui seria
              anunciar uma parede que a pessoa nao alcanca.
            */}
            {fonte.anexar ? (
              <div className="flex flex-col gap-cx-1">
                <span className="text-cx-sm font-medium text-texto">{t('Imagens')}</span>
                <CampoAnexo anexos={anexos} aoMudar={definirAnexos} desabilitado={enviando} />
              </div>
            ) : null}

            {/* Mesma decisao do `indisponivel` acima: guarda em portugues, traduz no render. */}
            {erroDoEnvio ? <p className="text-cx-sm text-erro">{t(erroDoEnvio)}</p> : null}

            <div className="flex justify-end">
              <Botao variante="primario" tamanho="sm" onClick={enviar} disabled={!podeEnviar}>
                {enviando ? t('Enviando…') : t('Enviar')}
              </Botao>
            </div>
          </div>
        ) : null}
      </section>

      {/* BLOCO 5 · MINHAS SOLICITACOES (passos 19 e 20), NA MESMA TELA. O manual diz que "nao
          ha caixa de entrada separada para lembrar de abrir", e uma segunda tela seria
          exatamente isso. */}
      {conversas.length > 0 ? (
        <section className="flex flex-col gap-cx-2">
          <h3 className="text-cx-xs font-semibold uppercase tracking-wide text-texto-fraco">
            {t('Minhas solicitações')}
          </h3>
          <ul className="flex flex-col gap-cx-3">
            {conversas.map((conversa) => (
              <li
                key={conversa.id}
                className="flex flex-col gap-cx-2 rounded-cx-md border border-borda bg-superficie p-cx-3"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-cx-2">
                  <span className="text-cx-base font-medium text-texto">{conversa.assunto}</span>
                  <span className="flex items-center gap-cx-2 text-cx-sm text-texto-fraco">
                    <span>{formatarData(conversa.em)}</span>
                    <span className="rounded-cx-sm bg-fundo px-cx-2 py-cx-1 text-cx-xs text-texto-suave">
                      {/* Traduzido NO PONTO DE USO: `ROTULO_DA_SITUACAO` e `Object.freeze`
                          avaliado na carga do modulo, e traduzi-lo la congelaria o idioma
                          do primeiro import. */}
                      {t(ROTULO_DA_SITUACAO[conversa.situacao])}
                    </span>
                  </span>
                </div>

                {conversa.categoria ? (
                  <span className="self-start rounded-cx-sm bg-fundo px-cx-2 py-cx-1 text-cx-xs text-texto-suave">
                    {conversa.categoria}
                  </span>
                ) : null}

                {/* O FIO, em ordem. Os turnos da equipe recuam para a direita, que e o que faz
                    a conversa ser lida como conversa e nao como lista de recados. */}
                <ol className="flex flex-col gap-cx-2">
                  {conversa.turnos.map((turno) => (
                    <li
                      key={turno.id}
                      className={
                        turno.autor === 'equipe'
                          ? 'ml-cx-4 rounded-cx-md bg-fundo p-cx-2'
                          : 'rounded-cx-md p-cx-2'
                      }
                    >
                      <span className="block text-cx-xs font-semibold uppercase tracking-wide text-texto-fraco">
                        {turno.autor === 'equipe' ? t('Equipe') : t('Você')}
                      </span>
                      <span className="block text-cx-sm text-texto">{turno.texto}</span>

                      {/*
                        OS ANEXOS DAQUELE TURNO. Pedido dele em 07/09/2026: "NA MENSAGEM QUE
                        COLOQUEI O ANEXO TEM QUE TER LOCAL PARA USUARIO CLICAR PARA VER."

                        O FILTRO E POR TURNO porque a busca traz os da CONVERSA inteira: uma
                        chamada em vez de uma por mensagem, e o recorte acontece aqui, que e
                        barato.
                      */}
                      {(anexosPorConversa[conversa.id] ?? []).filter(
                        (a) => a.mensagemId === turno.id,
                      ).length > 0 ? (
                        <ul className="mt-cx-1 flex flex-wrap gap-cx-2">
                          {(anexosPorConversa[conversa.id] ?? [])
                            .filter((a) => a.mensagemId === turno.id)
                            .map((a) => (
                              <li key={a.id}>
                                <Botao
                                  variante="secundario"
                                  tamanho="sm"
                                  onClick={() => abrirAnexo(a.id)}
                                  disabled={abrindoAnexo === a.id}
                                >
                                  {/* O NOME DO ARQUIVO nao passa por `t()`: quem o escreveu
                                      foi a pessoa, e traduzi-lo daria um arquivo que nao existe. */}
                                  {abrindoAnexo === a.id ? t('Abrindo…') : a.nomeArquivo}
                                </Botao>
                              </li>
                            ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ol>

                {/*
                  NAO HA CAMPO DE RESPOSTA, e a ausencia e decisao dele em 07/09/2026:
                  "AQUI TB NAO HAVERA TROCA DE MENSAGEM PELO PACIENTE NO MENU DE AJUDA".

                  O QUE CONTINUA EXISTINDO e ABRIR o chamado, no bloco "Nao encontrei o que
                  precisava" acima: aquilo foi decisao dele no MESMO dia, as 12:16, e tirar
                  as duas coisas deixaria a pessoa sem caminho nenhum para falar com a
                  equipe. O que saiu foi o VAIVEM: descreve uma vez, e a equipe responde.

                  A FRASE FICA NO LUGAR DO CAMPO, e nao o vazio: um bloco que some sem
                  explicacao faz quem ja usou procurar o campo achando que a tela quebrou.
                  Ausencia se diz.
                */}
                {conversa.situacao !== 'encerrada' ? (
                  <p className="text-cx-sm text-texto-suave">
                    {t(
                      'A nossa equipe responde por aqui. Você recebe um aviso quando houver resposta.',
                    )}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
